import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    selectHostUrl: String,
    selectOperatorUrl: String,
    hostSessionReady: Boolean,
    operatorSelectionReady: Boolean
  }

  static targets = [
    "host",
    "hostStatus",
    "operator",
    "operatorStatus",
    "newButton",
    "games",
    "gameSearch",
    "gameFilterCount",
    "gamesPanel",
    "legacyGamesNotice",
    "results",
    "selectedCount",
    "filtersPanel",
    "mode",
    "allFiltersPanel",
    "transactionPanel",
    "allFiltersTab",
    "transactionTab",
    "transactionId",
    "submitButton",
    "timeFrameId",
    "dateFrom",
    "dateTo"
  ]

  connect() {
    this.hostSelecting = false
    this.hostSessionReady = this.hostSessionReadyValue
    this.operatorSelecting = false
    this.operatorSelectionReady = this.operatorSelectionReadyValue
    this.filterOperators()
    this.showMode(this.modeTarget.value || "all_filters")
    this.updateNewButton()
    this.updateSelectedGamesCount()
    this.filterGames()
    this.initializePaginations()
    this.showResults()
  }

  showResults() {
    if (!this.hasResultsTarget) return

    requestAnimationFrame(() => {
      this.resultsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      this.resultsTarget.focus({ preventScroll: true })
    })
  }

  filterOperators() {
    const hostId = this.hostTarget.value
    const selectedOption = this.operatorTarget.selectedOptions[0]

    Array.from(this.operatorTarget.options).forEach((option) => {
      if (!option.value) return

      const belongsToHost = Boolean(hostId) && option.dataset.hostId === hostId
      option.hidden = !belongsToHost
      option.disabled = !belongsToHost
    })

    if (selectedOption && selectedOption.value && selectedOption.dataset.hostId !== hostId) {
      this.operatorTarget.value = ""
      this.operatorSelectionReady = false
      this.setOperatorStatus("")
    }

    this.operatorTarget.disabled = !hostId || !this.hostSessionReady
    this.updateNewButton()
  }

  updateNewButton() {
    if (!this.hasNewButtonTarget) return

    const disabled = !this.hostTarget.value ||
      this.hostSelecting ||
      !this.hostSessionReady ||
      !this.operatorTarget.value ||
      this.operatorSelecting ||
      !this.operatorSelectionReady
    this.newButtonTarget.disabled = disabled
    if (disabled && this.hasFiltersPanelTarget) this.filtersPanelTarget.classList.add("hidden")
  }

  async selectHost() {
    const selectedHostId = this.hostTarget.value
    this.hostSessionReady = false
    this.hostSelecting = Boolean(selectedHostId)
    this.operatorSelectionReady = false
    this.operatorTarget.value = ""
    this.setOperatorStatus("")
    this.filterOperators()

    if (!selectedHostId) {
      this.setHostStatus("")
      return
    }

    this.setHostStatus("Signing in to BackOfficePro…", "text-gray-500")
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams({ "gamecheck[host_id]": selectedHostId })

    try {
      const response = await fetch(this.selectHostUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": token
        },
        body: body.toString(),
        credentials: "same-origin"
      })
      const payload = await response.json()
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Unable to sign in")
      if (this.hostTarget.value !== selectedHostId) return

      this.hostSessionReady = true
      this.setHostStatus("Signed in successfully.", "text-green-700")
    } catch (error) {
      this.setHostStatus(error.message, "text-red-600")
    } finally {
      if (this.hostTarget.value === selectedHostId) {
        this.hostSelecting = false
        this.filterOperators()
      }
    }
  }

  async selectOperator() {
    const selectedOperatorId = this.operatorTarget.value
    this.operatorSelectionReady = false
    this.operatorSelecting = Boolean(selectedOperatorId)
    this.updateNewButton()

    if (!selectedOperatorId) {
      this.setOperatorStatus("")
      return
    }

    this.setOperatorStatus("Selecting the operator in BackOfficePro…", "text-gray-500")

    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams({
      "gamecheck[host_id]": this.hostTarget.value,
      "gamecheck[operatorinfo_id]": selectedOperatorId
    })

    try {
      const response = await fetch(this.selectOperatorUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": token
        },
        body: body.toString(),
        credentials: "same-origin"
      })
      const payload = await response.json()
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Unable to select the operator")
      if (this.operatorTarget.value !== selectedOperatorId) return

      this.operatorSelectionReady = true
      this.replaceGames(payload.games || [])
      this.setOperatorStatus("Operator selected successfully.", "text-green-700")
    } catch (error) {
      this.setOperatorStatus(error.message, "text-red-600")
    } finally {
      if (this.operatorTarget.value === selectedOperatorId) {
        this.operatorSelecting = false
        this.updateNewButton()
      }
    }
  }

  setOperatorStatus(message, colorClass = "text-gray-500") {
    if (!this.hasOperatorStatusTarget) return

    this.operatorStatusTarget.textContent = message
    this.operatorStatusTarget.classList.remove("text-gray-500", "text-green-700", "text-red-600")
    this.operatorStatusTarget.classList.add(colorClass)
  }

  replaceGames(games) {
    if (!this.hasGamesTarget) return

    this.gamesTarget.replaceChildren(...games.map((game) => {
      const option = document.createElement("option")
      option.value = game.value
      option.textContent = `${game.label} (${game.value})`
      option.selected = true
      return option
    }))
    this.updateSelectedGamesCount()
    this.filterGames()
  }

  setHostStatus(message, colorClass = "text-gray-500") {
    if (!this.hasHostStatusTarget) return

    this.hostStatusTarget.textContent = message
    this.hostStatusTarget.classList.remove("text-gray-500", "text-green-700", "text-red-600")
    this.hostStatusTarget.classList.add(colorClass)
  }

  openFilters() {
    if (this.newButtonTarget.disabled) return

    this.filtersPanelTarget.classList.remove("hidden")
    this.showMode(this.modeTarget.value || "all_filters")
  }

  closeFilters() {
    this.filtersPanelTarget.classList.add("hidden")
  }

  showAllFilters() {
    this.showMode("all_filters")
  }

  showByTransaction() {
    this.showMode("by_transaction")
  }

  showMode(mode) {
    const byTransaction = mode === "by_transaction"
    this.modeTarget.value = byTransaction ? "by_transaction" : "all_filters"
    this.allFiltersPanelTarget.classList.toggle("hidden", byTransaction)
    this.transactionPanelTarget.classList.toggle("hidden", !byTransaction)
    this.gamesPanelTarget.classList.toggle("hidden", byTransaction)
    this.legacyGamesNoticeTarget.classList.toggle("hidden", !byTransaction)
    this.setTabState(this.allFiltersTabTarget, !byTransaction)
    this.setTabState(this.transactionTabTarget, byTransaction)
    this.transactionIdTarget.required = byTransaction
    this.gamesTarget.required = !byTransaction
    this.submitButtonTarget.value = "Search"
  }

  setTabState(tab, active) {
    tab.classList.toggle("border-green-mga", active)
    tab.classList.toggle("bg-green-mga", active)
    tab.classList.toggle("text-white", active)
    tab.classList.toggle("border-gray-200", !active)
    tab.classList.toggle("bg-white", !active)
    tab.classList.toggle("text-gray-700", !active)
  }

  setPeriod(event) {
    const days = Number(event.params.days)
    if (!days || !this.hasDateFromTarget || !this.hasDateToTarget) return

    this.timeFrameIdTarget.value = String(event.params.timeFrame)
    const to = new Date()
    const from = new Date(to.getTime() - (days * 24 * 60 * 60 * 1000))
    this.dateFromTarget.value = this.toDateLocal(from)
    this.dateToTarget.value = this.toDateLocal(to)
  }

  setCustomPeriod() {
    this.timeFrameIdTarget.value = "4"
  }

  toDateLocal(date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  selectAllGames() {
    this.setAllGamesSelected(true)
  }

  deselectAllGames() {
    this.setAllGamesSelected(false)
  }

  setAllGamesSelected(selected) {
    if (!this.hasGamesTarget) return

    Array.from(this.gamesTarget.options).forEach((option) => {
      option.selected = selected
    })

    this.gamesTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateSelectedGamesCount() {
    if (!this.hasGamesTarget || !this.hasSelectedCountTarget) return

    const selected = Array.from(this.gamesTarget.selectedOptions).length
    const total = this.gamesTarget.options.length
    this.selectedCountTarget.textContent = `${selected} of ${total} selected`
  }

  filterGames() {
    if (!this.hasGamesTarget || !this.hasGameSearchTarget) return

    const query = this.gameSearchTarget.value.trim().toLocaleLowerCase()
    let visible = 0

    Array.from(this.gamesTarget.options).forEach((option) => {
      const matches = !query || option.textContent.toLocaleLowerCase().includes(query)
      option.hidden = !matches
      option.style.display = matches ? "" : "none"
      if (matches) visible += 1
    })

    if (this.hasGameFilterCountTarget) {
      const total = this.gamesTarget.options.length
      this.gameFilterCountTarget.textContent = query
        ? `Showing ${visible} of ${total} games`
        : `${total} games available`
    }
  }

  clearGameSearch() {
    if (!this.hasGameSearchTarget) return

    this.gameSearchTarget.value = ""
    this.filterGames()
    this.gameSearchTarget.focus()
  }

  initializePaginations() {
    this.element.querySelectorAll(".gamecheck-pagination").forEach((pagination) => {
      this.renderPagination(pagination)
    })
  }

  async loadResultPage(event) {
    const button = event.currentTarget
    const pagination = button.closest(".gamecheck-pagination")
    const status = pagination.querySelector(".gamecheck-pagination-status")
    const tbody = document.getElementById(pagination.dataset.gamecheckTargetId)
    const requestedPage = Number(button.dataset.gamecheckPage)
    if (!tbody) return

    pagination.querySelectorAll("button").forEach((item) => { item.disabled = true })
    status.textContent = ""

    try {
      const url = new URL(pagination.dataset.gamecheckPaginationUrl, window.location.origin)
      url.searchParams.set("gamecheck[page]", String(requestedPage))
      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      const payload = await response.json()
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Unable to load the page")

      tbody.replaceChildren(...payload.rows.map((row) => this.buildResultRow(row)))
      this.updateTableCaption(tbody.closest("table"))
      pagination.dataset.gamecheckCurrentPage = String(payload.page)
      pagination.dataset.gamecheckTotalPages = String(payload.total_pages)
      this.renderPagination(pagination)
    } catch (error) {
      pagination.querySelectorAll("button").forEach((item) => { item.disabled = false })
      status.textContent = error.message
    }
  }

  renderPagination(pagination) {
    const current = Number(pagination.dataset.gamecheckCurrentPage)
    const total = Number(pagination.dataset.gamecheckTotalPages)
    const status = pagination.querySelector(".gamecheck-pagination-status")
    pagination.querySelectorAll("button").forEach((button) => button.remove())

    const pages = []
    const start = Math.max(1, Math.min(current - 2, total - 4))
    const end = Math.min(total, start + 4)
    for (let page = start; page <= end; page += 1) pages.push(page)

    const controls = [
      ["First", 1],
      ["«", Math.max(1, current - 1)],
      ...pages.map((page) => [String(page), page]),
      ["»", Math.min(total, current + 1)],
      ["Last", total]
    ]

    controls.forEach(([label, page]) => {
      const button = document.createElement("button")
      button.type = "button"
      button.textContent = label
      button.dataset.gamecheckPage = String(page)
      button.className = `gamecheck-page-button${page === current && /^\d+$/.test(label) ? " is-active" : ""}`
      button.disabled = page === current
      button.addEventListener("click", (event) => this.loadResultPage(event))
      pagination.insertBefore(button, status)
    })
  }

  buildResultRow(row) {
    const tr = document.createElement("tr")
    ;[
      row.date,
      row.transaction_id,
      row.player_id,
      row.bet,
      row.win,
      row.jackpot_win,
      row.win_consumed,
      row.has_bonus_game == null ? "" : String(row.has_bonus_game)
    ].forEach((value) => {
      const td = document.createElement("td")
      td.textContent = value ?? ""
      tr.appendChild(td)
    })

    const actions = document.createElement("td")
    actions.append(
      this.resultLink(row.detail_url, "View details", "View details"),
      this.resultLink(row.view_url, "👁️", "View game round", true),
      this.resultLink(row.following_url, "➕", "View this game round and the following ones", true)
    )
    tr.appendChild(actions)
    return tr
  }

  resultLink(url, label, title, external = false) {
    const link = document.createElement("a")
    link.href = url
    link.textContent = label
    link.title = title
    link.setAttribute("aria-label", title)
    link.className = label.length <= 2 ? "gamecheck-action gamecheck-action--emoji" : "gamecheck-action"
    if (external) {
      link.target = "_blank"
      link.rel = "noopener noreferrer"
    }
    return link
  }

  updateTableCaption(table) {
    const caption = table?.querySelector("caption")
    if (!caption) return

    const count = table.querySelectorAll("tbody tr").length
    const base = caption.textContent.replace(/ · \d+ game rounds? shown$/, "")
    caption.textContent = `${base} · ${count} ${count === 1 ? "game round shown" : "game rounds shown"}`
  }
}
